// Inline tracked changes on a knowledge page (plan §5 UI): the server gives
// each open suggestion's changes as ranges of the current Markdown; this
// module decides how the page shows them. A change is word-diffed and its
// removed and inserted words are carried through the ordinary Markdown parse
// by private-use sentinel characters, which WorkspaceMarkdown turns into
// <del>/<ins> marks and a numbered marker. Before trusting that, the result is
// checked by parsing: with the inserted words taken out it must read as the
// current text, and with the removed words taken out as the suggested text.
// A change that alters block structure (a paragraph that becomes a list, a
// change inside a link address) fails that check and is shown as whole blocks
// removed and added instead. Nothing here reads the words themselves.
import { exactWordDiff } from '../document-diff';
import {
  closeMark,
  countSentinels,
  hasSentinels,
  MARK_LIMIT,
  markerOf,
  openMark,
  splitMarked,
} from '../markdown-marks';
import { parseMarkdownTree, type MarkdownNode } from '../markdown-parse';

export { MARK_LIMIT } from '../markdown-marks';

/** A change as a range of the UTF-16 text it applies to. */
export interface InlineChange {
  start: number;
  end: number;
  deleted: string;
  inserted: string;
}

/** One suggestion's changes; `index` numbers its marks (0 based). */
export interface MarkInput {
  index: number;
  changes: InlineChange[];
}

export type Chunk =
  | { kind: 'plain'; key: string; text: string; sectionIds: string[] }
  /** Inline marks: `text` carries sentinels for WorkspaceMarkdown. */
  | { kind: 'marked'; key: string; text: string; sectionIds: string[]; indexes: number[] }
  /** Whole blocks: unchanged, removed and added, in reading order. */
  | {
      kind: 'blocks';
      key: string;
      parts: BlockPart[];
      sectionIds: string[];
      indexes: number[];
    };

export interface BlockPart {
  kind: 'equal' | 'remove' | 'add';
  text: string;
}

export interface ChunkPlan {
  chunks: Chunk[];
  /** Suggestions that could not be placed in the text (their changes overlap another's). */
  unplaced: Set<number>;
}

/**
 * Code point offsets (the server's convention) to UTF-16 offsets of `text`.
 * Returns null for an offset past the end.
 */
export function codePointOffsets(text: string) {
  const offsets: number[] = [];
  let unit = 0;
  for (const char of text) {
    offsets.push(unit);
    unit += char.length;
  }
  offsets.push(unit);
  return (point: number) => (point >= 0 && point < offsets.length ? offsets[point] : null);
}

/**
 * The server's code point changes as UTF-16 changes of `markdown`, or null
 * when any change's range does not hold the text it says it removes (the
 * suggestion is then shown on its card only).
 */
export function toInlineChanges(
  markdown: string,
  changes: { start: number; end: number; deleted: string; inserted: string }[],
): InlineChange[] | null {
  const unit = codePointOffsets(markdown);
  const result: InlineChange[] = [];
  for (const change of changes) {
    const start = unit(change.start);
    const end = unit(change.end);
    if (start === null || end === null || markdown.slice(start, end) !== change.deleted)
      return null;
    result.push({ start, end, deleted: change.deleted, inserted: change.inserted });
  }
  return result;
}

interface Block {
  start: number;
  end: number;
}

function topBlocks(markdown: string): Block[] {
  return (parseMarkdownTree(markdown).children ?? [])
    .map((node) => ({
      start: node.position?.start.offset ?? -1,
      end: node.position?.end.offset ?? -1,
    }))
    .filter((block) => block.start >= 0 && block.end >= block.start);
}

/** The first and last block a change touches; a change between blocks joins the block before it. */
function spanOf(blocks: Block[], change: InlineChange): [number, number] | null {
  let first = -1;
  let last = -1;
  blocks.forEach((block, index) => {
    const hit =
      change.end > change.start
        ? block.start < change.end && block.end > change.start
        : block.start <= change.start && change.start <= block.end;
    if (!hit) return;
    if (first < 0) first = index;
    last = index;
  });
  if (first >= 0) return [first, last];
  for (let index = blocks.length - 1; index >= 0; index -= 1)
    if (blocks[index].end <= change.start) return [index, index];
  return blocks.length ? [0, 0] : null;
}

interface PlacedChange extends InlineChange {
  index: number;
}

interface Region {
  first: number;
  last: number;
  start: number;
  end: number;
  changes: PlacedChange[];
}

/** Changes sorted by position; one that overlaps an earlier suggestion's change is not placed. */
function placeChanges(suggestions: MarkInput[]) {
  const all = suggestions
    .filter((suggestion) => suggestion.index < MARK_LIMIT)
    .flatMap((suggestion) =>
      suggestion.changes.map((change) => ({ ...change, index: suggestion.index })),
    )
    .sort((a, b) => a.start - b.start || a.end - b.end || a.index - b.index);
  const unplaced = new Set(
    suggestions.filter((suggestion) => suggestion.index >= MARK_LIMIT).map((item) => item.index),
  );
  const placed: PlacedChange[] = [];
  for (const change of all) {
    const previous = placed[placed.length - 1];
    const overlaps =
      previous &&
      (change.start < previous.end ||
        (change.start === previous.start && change.start === change.end));
    if (overlaps && previous.index !== change.index) unplaced.add(change.index);
    else placed.push(change);
  }
  // A suggestion is marked whole or not at all.
  return { placed: placed.filter((change) => !unplaced.has(change.index)), unplaced };
}

function regionsOf(markdown: string, blocks: Block[], changes: PlacedChange[]): Region[] {
  const regions: Region[] = [];
  for (const change of changes) {
    const span = spanOf(blocks, change);
    const first = span ? span[0] : -1;
    const last = span ? span[1] : -1;
    const start = Math.min(span ? blocks[first].start : 0, change.start);
    const end = Math.max(span ? blocks[last].end : markdown.length, change.end);
    const open = regions[regions.length - 1];
    if (open && (first <= open.last || start < open.end)) {
      open.last = Math.max(open.last, last);
      open.end = Math.max(open.end, end);
      open.start = Math.min(open.start, start);
      open.changes.push(change);
    } else regions.push({ first, last, start, end, changes: [change] });
  }
  return regions;
}

const isSpace = (char: string) => char.trim() === '';

/**
 * One removed or inserted run. Whitespace at its edges stays outside the
 * marks, so a mark never opens at the end of one block and closes in the next.
 */
function segment(kind: 'del' | 'ins', index: number, text: string) {
  let from = 0;
  let to = text.length;
  while (from < to && isSpace(text[from])) from += 1;
  while (to > from && isSpace(text[to - 1])) to -= 1;
  if (from === to) return text;
  return `${text.slice(0, from)}${openMark(kind, index)}${text.slice(from, to)}${closeMark()}${text.slice(to)}`;
}

function markedChange(change: PlacedChange) {
  return exactWordDiff(change.deleted, change.inserted)
    .map((word) =>
      word.kind === 'equal'
        ? word.text
        : segment(word.kind === 'remove' ? 'del' : 'ins', change.index, word.text),
    )
    .join('');
}

// ---------------------------------------------------------------------------
// The parse check.

const TEXT_NODES = new Set(['text', 'inlineCode', 'code', 'html']);
/** Nodes without text that a reader still sees. */
const MARK_NODES = new Set(['thematicBreak', 'image']);

function nodeLabel(node: MarkdownNode) {
  if (node.type === 'heading') return `heading${node.depth ?? ''}`;
  if (node.type === 'list') return node.ordered ? 'list1' : 'list';
  return node.type;
}

type Reading = 'current' | 'suggested' | 'plain';

/**
 * What a reader sees in a parsed tree, as a list of "path:text" entries: the
 * text of each inline container, with the words of the other reading taken
 * out. `hidden` counts sentinels the renderer would never see.
 */
function reading(tree: MarkdownNode, mode: Reading) {
  const entries: { parent: MarkdownNode; path: string; text: string }[] = [];
  let state: 'none' | 'del' | 'ins' = 'none';
  let seen = 0;
  const visit = (node: MarkdownNode, path: string, parent: MarkdownNode) => {
    const label = `${path}/${nodeLabel(node)}`;
    const keep = () =>
      mode === 'plain' ||
      (mode === 'current' && state !== 'ins') ||
      (mode === 'suggested' && state !== 'del');
    if (MARK_NODES.has(node.type)) {
      entries.push({ parent, path: label, text: keep() ? `<${node.type}>` : '' });
      return;
    }
    if (TEXT_NODES.has(node.type) && typeof node.value === 'string') {
      let text = '';
      for (const piece of splitMarked(node.value)) {
        if (typeof piece === 'string') {
          if (keep()) text += piece;
          continue;
        }
        seen += 1;
        if (piece.kind === 'close') state = 'none';
        else if (piece.kind !== 'mark') state = piece.kind;
      }
      const previous = entries[entries.length - 1];
      if (previous && previous.parent === parent && previous.path === label) previous.text += text;
      else entries.push({ parent, path: label, text });
      return;
    }
    for (const child of node.children ?? []) visit(child, label, node);
  };
  for (const child of tree.children ?? []) visit(child, '', tree);
  const lines = entries
    // Spacing is not compared: whitespace at the edge of a mark stays outside it.
    .map((entry) => ({ path: entry.path, text: entry.text.split(/\s+/u).join('') }))
    .filter((entry) => entry.text)
    .map((entry) => `${entry.path}:${entry.text}`);
  return { lines, seen };
}

const same = (left: string[], right: string[]) =>
  left.length === right.length && left.every((line, index) => line === right[index]);

/** True when the marked text reads as `before` and `after` and every mark is visible. */
export function marksReadCorrectly(marked: string, before: string, after: string) {
  const tree = parseMarkdownTree(marked);
  const current = reading(tree, 'current');
  if (current.seen !== countSentinels(marked)) return false;
  return (
    same(current.lines, reading(parseMarkdownTree(before), 'plain').lines) &&
    same(reading(tree, 'suggested').lines, reading(parseMarkdownTree(after), 'plain').lines)
  );
}

// ---------------------------------------------------------------------------
// The plan.

/** Marked text of one change, with a marker before any whitespace it ends with. */
function withMarker(text: string, marker: string) {
  let end = text.length;
  while (end > 0 && isSpace(text[end - 1])) end -= 1;
  return `${text.slice(0, end)}${marker}${text.slice(end)}`;
}

function regionChunk(
  markdown: string,
  region: Region,
  sectionIds: string[],
  markers: boolean,
): Chunk {
  const before = markdown.slice(region.start, region.end);
  let after = '';
  let marked = '';
  let position = region.start;
  // Each suggestion's marker follows its last change in this passage.
  const last = new Map(region.changes.map((change, order) => [change.index, order]));
  region.changes.forEach((change, order) => {
    const kept = markdown.slice(position, change.start);
    after += kept + change.inserted;
    const text = markedChange(change);
    marked +=
      kept +
      (markers && last.get(change.index) === order
        ? withMarker(text, markerOf(change.index))
        : text);
    position = change.end;
  });
  const marker = new Set(region.changes.map((change) => change.index));
  after += markdown.slice(position, region.end);
  marked += markdown.slice(position, region.end);
  const indexes = [...marker];
  const key = `r${region.start}`;
  const safe = !hasSentinels(before) && !hasSentinels(after);
  if (safe && marksReadCorrectly(marked, before, after))
    return { kind: 'marked', key, text: marked, sectionIds, indexes };
  return { kind: 'blocks', key, parts: blockParts(before, after), sectionIds, indexes };
}

/** Two texts as whole top-level blocks: kept, removed and added, in reading order. */
export function blockParts(before: string, after: string): BlockPart[] {
  const left = topBlocks(before).map((block) => before.slice(block.start, block.end));
  const right = topBlocks(after).map((block) => after.slice(block.start, block.end));
  return alignBlocks(left, right).map(([a, b]) =>
    a !== null && b !== null
      ? { kind: 'equal', text: left[a] }
      : a !== null
        ? { kind: 'remove', text: left[a] }
        : { kind: 'add', text: right[b!] },
  );
}

/**
 * One suggestion's changes as the whole blocks it removes and adds, for its
 * card when the page cannot mark them (they overlap another suggestion's).
 * Accepting applies exactly these changes, so the card shows all of them.
 */
export function changePreview(markdown: string, changes: InlineChange[]): BlockPart[] {
  if (!changes.length) return [];
  const blocks = topBlocks(markdown);
  let start = Infinity;
  let end = -Infinity;
  for (const change of changes) {
    const span = spanOf(blocks, change);
    start = Math.min(start, change.start, span ? blocks[span[0]].start : change.start);
    end = Math.max(end, change.end, span ? blocks[span[1]].end : change.end);
  }
  let after = '';
  let position = start;
  for (const change of [...changes].sort((a, b) => a.start - b.start)) {
    after += markdown.slice(position, change.start) + change.inserted;
    position = change.end;
  }
  after += markdown.slice(position, end);
  return blockParts(markdown.slice(start, end), after).filter((part) => part.kind !== 'equal');
}

/** For each block, the section (by id) it starts, from the server's UTF-16 section starts. */
function sectionStarts(blocks: Block[], sections: { id: string; start: number }[]) {
  const starts = new Map<number, string>();
  for (const section of sections) {
    const index = blocks.findIndex((block) => block.start >= section.start);
    if (index >= 0 && !starts.has(index)) starts.set(index, section.id);
  }
  return starts;
}

/**
 * The page as a list of chunks to render in order: plain runs of blocks (a
 * new one at every section start, so sections keep their anchors) and one
 * chunk per group of overlapping changes. `markers: false` leaves out the
 * numbered markers (a history comparison).
 */
export function planChunks(
  markdown: string,
  suggestions: MarkInput[],
  sections: { id: string; start: number }[] = [],
  { markers = true }: { markers?: boolean } = {},
): ChunkPlan {
  const blocks = topBlocks(markdown);
  const { placed, unplaced } = placeChanges(suggestions);
  const regions = regionsOf(markdown, blocks, placed);
  const starts = sectionStarts(blocks, sections);
  const chunks: Chunk[] = [];
  let plainFrom = 0;
  let plainSections: string[] = [];
  const flush = (until: number) => {
    const text = markdown.slice(plainFrom, until);
    if (text.trim())
      chunks.push({ kind: 'plain', key: `p${plainFrom}`, text, sectionIds: plainSections });
    plainSections = [];
  };
  // Every section a region's blocks start keeps its anchor on that chunk.
  const regionSections = (region: Region) => {
    const ids: string[] = [];
    for (let index = Math.max(region.first, 0); index <= region.last; index += 1) {
      const id = starts.get(index);
      if (id) ids.push(id);
    }
    return ids;
  };
  let regionIndex = 0;
  for (let index = 0; index < blocks.length; index += 1) {
    const region = regions[regionIndex];
    if (region && region.first >= 0 && region.first <= index) {
      flush(region.start);
      chunks.push(regionChunk(markdown, region, regionSections(region), markers));
      plainFrom = region.end;
      index = Math.max(index, region.last);
      regionIndex += 1;
      continue;
    }
    const section = starts.get(index);
    if (section && blocks[index].start >= plainFrom) {
      if (blocks[index].start > plainFrom) flush(blocks[index].start);
      plainFrom = blocks[index].start;
      plainSections = [...plainSections, section];
    }
  }
  // A page without blocks (no text yet) takes its changes as one region.
  for (; regionIndex < regions.length; regionIndex += 1) {
    const region = regions[regionIndex];
    flush(region.start);
    chunks.push(regionChunk(markdown, region, [], markers));
    plainFrom = region.end;
  }
  flush(markdown.length);
  return { chunks, unplaced };
}

// ---------------------------------------------------------------------------
// Differences between two revisions, as changes of the earlier one.

const MAX_BLOCK_MATRIX = 250_000;

/**
 * The block-level difference between two revisions as changes of `before`:
 * each run of differing top-level blocks becomes one change, which the page
 * then word-diffs like a suggestion.
 */
export function blockChanges(before: string, after: string): InlineChange[] {
  if (before === after) return [];
  const left = topBlocks(before);
  const right = topBlocks(after);
  const source = (text: string, block: Block) => text.slice(block.start, block.end);
  const leftText = left.map((block) => source(before, block));
  const rightText = right.map((block) => source(after, block));
  let head = 0;
  while (head < left.length && head < right.length && leftText[head] === rightText[head]) head += 1;
  let tail = 0;
  while (
    tail < left.length - head &&
    tail < right.length - head &&
    leftText[left.length - 1 - tail] === rightText[right.length - 1 - tail]
  )
    tail += 1;
  const oldMiddle = leftText.slice(head, left.length - tail);
  const newMiddle = rightText.slice(head, right.length - tail);
  const pairs = alignBlocks(oldMiddle, newMiddle);
  const changes: InlineChange[] = [];
  let run: { from: number; to: number; newFrom: number; newTo: number } | null = null;
  const close = () => {
    if (!run) return;
    changes.push(runChange(before, after, left, right, run));
    run = null;
  };
  let i = head;
  let j = head;
  for (const [a, b] of pairs) {
    if (a !== null && b !== null) {
      close();
      i = head + a + 1;
      j = head + b + 1;
      continue;
    }
    run ??= { from: i, to: i, newFrom: j, newTo: j };
    if (a !== null) run.to = head + a + 1;
    if (b !== null) run.newTo = head + b + 1;
    i = run.to;
    j = run.newTo;
  }
  close();
  return changes;
}

/** Pairs of equal block positions (LCS), or null on the side that has no counterpart. */
function alignBlocks(left: string[], right: string[]): Array<[number | null, number | null]> {
  if (left.length * right.length > MAX_BLOCK_MATRIX)
    return [
      ...left.map((_, index) => [index, null] as [number, null]),
      ...right.map((_, index) => [null, index] as [null, number]),
    ];
  const table = Array.from({ length: left.length + 1 }, () => new Uint32Array(right.length + 1));
  for (let i = left.length - 1; i >= 0; i -= 1)
    for (let j = right.length - 1; j >= 0; j -= 1)
      table[i][j] =
        left[i] === right[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
  const pairs: Array<[number | null, number | null]> = [];
  let i = 0;
  let j = 0;
  while (i < left.length || j < right.length) {
    if (i < left.length && j < right.length && left[i] === right[j]) pairs.push([i++, j++]);
    else if (i < left.length && (j === right.length || table[i + 1][j] >= table[i][j + 1]))
      pairs.push([i++, null]);
    else pairs.push([null, j++]);
  }
  return pairs;
}

function runChange(
  before: string,
  after: string,
  left: Block[],
  right: Block[],
  run: { from: number; to: number; newFrom: number; newTo: number },
): InlineChange {
  const inserted =
    run.newTo > run.newFrom ? after.slice(right[run.newFrom].start, right[run.newTo - 1].end) : '';
  if (run.to > run.from) {
    const start = left[run.from].start;
    const end = left[run.to - 1].end;
    return { start, end, deleted: before.slice(start, end), inserted };
  }
  // Only new blocks: they go after the block before them, or before the first one.
  if (run.from > 0) {
    const at = left[run.from - 1].end;
    return { start: at, end: at, deleted: '', inserted: `\n\n${inserted}` };
  }
  const at = left.length ? left[0].start : before.length;
  return { start: at, end: at, deleted: '', inserted: left.length ? `${inserted}\n\n` : inserted };
}
